<template>
  <div class="terms">
    <div class="card">
      <h1 class="page-title">Terms of Service</h1>
      <p class="last-updated">Last Updated: {{ new Date().toLocaleDateString() }}</p>
      
      <div class="terms-content">
        <section class="terms-section">
          <h2>Introduction</h2>
          <p>Welcome to ImageOptimizer. These Terms of Service ("Terms") govern your use of the ImageOptimizer website and services (the "Service"). By using our Service, you agree to these Terms. If you disagree with any part of the terms, you may not use our Service.</p>
        </section>
        
        <section class="terms-section">
          <h2>Acceptance of Terms</h2>
          <p>By accessing or using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms. We reserve the right to modify these Terms at any time. Your continued use of the Service following the posting of revised Terms means that you accept and agree to the changes.</p>
        </section>
        
        <section class="terms-section">
          <h2>Description of Service</h2>
          <p>ImageOptimizer provides an online platform for compressing image files to reduce their file size while maintaining acceptable visual quality. The Service allows you to upload images, compress them using our proprietary algorithms, and download the compressed images for your use.</p>
        </section>
        
        <section class="terms-section">
          <h2>User Accounts</h2>
          <p>Some features of the Service may require you to create an account. You are responsible for maintaining the confidentiality of your account information and password. You agree to accept responsibility for all activities that occur under your account. If you believe your account has been compromised, please contact us immediately.</p>
        </section>
        
        <section class="terms-section">
          <h2>User Content</h2>
          <p>Our Service allows you to upload, download, and share content, including images. By uploading content to our Service, you:</p>
          <ul>
            <li>Warrant that you have the legal right to upload and use the content</li>
            <li>Grant us a non-exclusive license to use, store, and process your content for the purpose of providing the Service</li>
            <li>Understand that you retain all ownership rights to your content</li>
          </ul>
          <p>We do not claim ownership of your content, but we need certain rights to provide the Service. You are solely responsible for the content you upload and the consequences of sharing it.</p>
        </section>
        
        <section class="terms-section">
          <h2>Prohibited Uses</h2>
          <p>You agree not to use the Service for any purpose that is unlawful or prohibited by these Terms. Prohibited uses include, but are not limited to:</p>
          <ul>
            <li>Uploading illegal, harmful, threatening, abusive, or offensive content</li>
            <li>Attempting to compromise the security of the Service or other users' accounts</li>
            <li>Using automated methods to access or use the Service in a way that sends more requests to our servers than a person could reasonably produce in the same period</li>
            <li>Interfering with or disrupting the Service or servers or networks connected to the Service</li>
            <li>Engaging in any activity that could damage, disable, or impair the Service</li>
          </ul>
        </section>
        
        <section class="terms-section">
          <h2>Intellectual Property</h2>
          <p>The Service and its original content (excluding content provided by users), features, and functionality are and will remain the exclusive property of ImageOptimizer and its licensors. The Service is protected by copyright, trademark, and other laws. Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.</p>
        </section>
        
        <section class="terms-section">
          <h2>DMCA and Copyright Infringement</h2>
          <p>We respect the intellectual property rights of others. If you believe that material available on our Service infringes on your copyright, please notify us according to the Digital Millennium Copyright Act (DMCA) procedures. We will respond to notices of alleged copyright infringement that comply with applicable law and terminate accounts of repeat infringers.</p>
        </section>
        
        <section class="terms-section">
          <h2>Limitation of Liability</h2>
          <p>In no event shall ImageOptimizer, its directors, employees, partners, agents, suppliers, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:</p>
          <ul>
            <li>Your access to or use of or inability to access or use the Service</li>
            <li>Any conduct or content of any third party on the Service</li>
            <li>Any content obtained from the Service</li>
            <li>Unauthorized access, use, or alteration of your transmissions or content</li>
          </ul>
        </section>
        
        <section class="terms-section">
          <h2>Disclaimer of Warranties</h2>
          <p>Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE" basis. The Service is provided without warranties of any kind, whether express or implied, including, but not limited to, implied warranties of merchantability, fitness for a particular purpose, non-infringement, or course of performance.</p>
        </section>
        
        <section class="terms-section">
          <h2>Indemnification</h2>
          <p>You agree to defend, indemnify, and hold harmless ImageOptimizer and its licensees and licensors, and their employees, contractors, agents, officers, and directors from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses arising from your use of the Service.</p>
        </section>
        
        <section class="terms-section">
          <h2>Governing Law</h2>
          <p>These Terms shall be governed by and defined following the laws of [Your Jurisdiction]. ImageOptimizer and yourself irrevocably consent that the courts of [Your Jurisdiction] shall have exclusive jurisdiction to resolve any dispute which may arise in connection with these Terms.</p>
        </section>
        
        <section class="terms-section">
          <h2>Changes to Terms</h2>
          <p>We reserve the right to modify or replace these Terms at any time. It is your responsibility to review these Terms periodically for changes. Your continued use of the Service following the posting of revised Terms means that you accept and agree to the changes.</p>
        </section>
        
        <section class="terms-section">
          <h2>Contact Us</h2>
          <p>If you have any questions about these Terms, please contact us:</p>
          <div class="contact-info">
            <p>Email: terms@imageoptimizer.com</p>
            <p>Address: 123 Compression Street, Web City, 10101</p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Terms'
}
</script>

<style scoped>
.terms {
  max-width: 800px;
  margin: 0 auto;
}

.page-title {
  color: var(--dark-color);
  font-size: 2.2rem;
  margin-bottom: 0.5rem;
  text-align: center;
  position: relative;
  padding-bottom: 1rem;
}

.page-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 4px;
  background: linear-gradient(to right, var(--primary-color), var(--accent-color));
  border-radius: 2px;
}

.last-updated {
  text-align: center;
  color: var(--light-text);
  font-size: 0.9rem;
  margin-bottom: 2rem;
}

.terms-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.terms-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.terms-section h2 {
  color: var(--dark-color);
  font-size: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 0.5rem;
}

.terms-section p {
  font-size: 1rem;
  line-height: 1.7;
  color: var(--text-color);
}

.terms-section ul {
  padding-left: 1.5rem;
  margin-top: 0.5rem;
}

.terms-section li {
  margin-bottom: 0.5rem;
  font-size: 1rem;
  line-height: 1.7;
}

.contact-info {
  background-color: var(--secondary-color);
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
}

.contact-info p {
  margin: 0.5rem 0;
}

@media (max-width: 768px) {
  .page-title {
    font-size: 1.8rem;
  }
  
  .terms-section h2 {
    font-size: 1.3rem;
  }
  
  .terms-section p,
  .terms-section li {
    font-size: 0.95rem;
  }
}
</style> 